import { paths } from '@farmersdog/constants';
import { Banner, Link, Text } from '@farmersdog/corgi-x';
import {
  useCoupon,
  usePromotion,
  isContextPrimary,
  DiscountType,
  getDiscountBannerCopy,
} from '@farmersdog/coupons';
import type { PromotionContextTreatment } from '@farmersdog/coupons';
import { isUserReferrerResponse } from '@farmersdog/coupons/src/utils/discount';
import { Logger } from '@farmersdog/logger';

import { FeatureName } from '../../abTesting/features';
import { useContentFeature } from '../../abTesting/useContentFeature';
import config from '../../config';

import styles from './DiscountBanner.css';
import { getBannerCtaCopy } from './utils';

const shadowLogger = new Logger('content:banner-copy-shadow');

export function DiscountBanner() {
  const { treatment: rawTreatment } = useContentFeature(
    FeatureName.MT_DISCOUNT_CONTEXT,
    {}
  );
  const treatment = rawTreatment as PromotionContextTreatment;

  const legacy = useCoupon({
    apiUrl: `${config.get('app.apiUrl')}/referrer`,
    defaultDiscount: {
      discountAmount: config.get('features.defaultDiscountPercentage'),
      discountType: DiscountType.percentage,
    },
  });

  const context = usePromotion();

  const { loading, discountAmount, discountType, coupon, isExpired } =
    isContextPrimary(treatment) ? context : legacy;

  const bannerCtaCopy = getBannerCtaCopy({ discountAmount });
  const bannerCopy = getDiscountBannerCopy({
    coupon: coupon,
    discountAmount,
    discountType,
    isExpired,
  });

  // Scope to FE's user-referrer interpolation. Expired/zero-discount branches
  // intentionally override the user-referrer template — they're not noise for
  // this PR — we're verifying the interpolation == BE bannerCopy before ripping
  // FE interpolation out. (No overrideCopy is passed here.)
  if (
    !isExpired &&
    discountAmount > 0 &&
    coupon &&
    isUserReferrerResponse(coupon) &&
    coupon.bannerCopy &&
    bannerCopy !== coupon.bannerCopy
  ) {
    shadowLogger.info('banner-copy shadow mismatch', {
      site: 'acquisition:DiscountBanner',
      feCopy: bannerCopy,
      beCopy: coupon.bannerCopy,
      firstName: coupon.name,
      discountAmount,
      discountType,
      discountPercentage: coupon.discountPercentage,
    });
  }

  return (
    <Banner>
      {!loading && (
        <Link href={paths.PATH_SIGNUP}>
          <Text
            className={styles.discountBanner}
            variant="heading-20"
            weight="semibold"
            color="white"
            as="h5"
          >
            {bannerCopy} <strong className={styles.cta}>{bannerCtaCopy}</strong>
          </Text>
        </Link>
      )}
    </Banner>
  );
}
